import { affiliateUrl } from "@/lib/affiliate";
import { formatPrice, safeUrl } from "@/lib/utils";
import { piecePriceLabels } from "@/lib/mum-hub/offers";
import type { MumHubUnlockedItem } from "@/lib/mum-hub/api";
import type { Product } from "@shared/schema";
import type { CardLinkTarget } from "@/components/products/ProductCardShell";

/**
 * The little that a card needs to know about a thing it is selling.
 *
 * The shop has a catalogue `Product` with forty fields and the Mum Hub has a
 * piece from a curated look. Neither can be expressed in the other, so this is
 * the intersection, and it is what `ProductCardShell` draws. One adapter per
 * surface, below, and no third way in.
 *
 * Prices arrive ALREADY FORMATTED. That looks like an odd choice for a data
 * type and it is deliberate: the shop prints "£98.00" through `formatPrice`
 * and the Mum Hub prints "£45" through `formatPence`, which drops the pennies
 * on a whole pound. Handing the card two numbers and a currency would force it
 * to pick one of those, and one of the two surfaces would silently change the
 * way it writes money.
 */
export interface CardProduct {
  id: string;
  name: string;
  brand: string | null;
  /** What she pays, formatted. */
  priceLabel: string | null;
  /**
   * The regular price, formatted, when this is a sale and only then. The
   * comparison is the adapter's job: a card handed two prices would have to
   * decide what "on sale" means, and the two surfaces store it differently.
   */
  originalPriceLabel: string | null;
  /** Every photograph, the main one first, deduplicated where the source needs it. */
  images: string[];
  /**
   * The merchant URL, through `safeUrl`, NOT through `affiliateUrl`. Null when
   * there is nowhere to buy it. Wrapping happens in {@link cardLinkFor}, once.
   */
  url: string | null;
  /**
   * Whether we have an affiliate arrangement with the shop behind {@link url}.
   *
   * Only `true` wraps and only `true` discloses. An unwrapped link to an
   * affiliated shop loses a commission; a wrapped link to a shop we have no
   * arrangement with sends a customer through a redirect that earns nothing
   * and carries a `sponsored` rel disclosing a paid relationship that does not
   * exist. The second is wrong on the page and not only in the accounts.
   */
  affiliate: boolean;
  /** The xcust marker on a wrapped link, or null when there is nothing to mark it with. */
  affiliateId: string | null;
  /** What an outbound click reports as the thing that was clicked. */
  trackingSlug: string;
}

/** Every outbound merchant link says this much, affiliated or not. */
const BASE_REL = "noopener noreferrer nofollow";

/**
 * Where a card links to, wrapped and disclosed according to the arrangement.
 *
 * Null when there is nowhere to buy the thing, so the caller draws words
 * rather than an anchor. An anchor to nowhere, or one that looks like every
 * other tile and does nothing, is worse than saying so plainly.
 */
export function cardLinkFor(
  card: CardProduct,
  extra: Pick<CardLinkTarget, "label" | "onActivate"> = {},
): CardLinkTarget | null {
  if (!card.url) return null;
  return {
    href: card.affiliate
      ? affiliateUrl(card.url, card.affiliateId ?? undefined)
      : card.url,
    rel: card.affiliate ? `${BASE_REL} sponsored` : BASE_REL,
    ...extra,
  };
}

/**
 * A catalogue product, as the card sees it.
 *
 * Everything in the shop is affiliated, which is why the shop never had a flag
 * for it. It gets one here so the shape is honest rather than so the shop's
 * behaviour changes: `affiliate: true` reproduces exactly what the shop did.
 *
 * The photographs are NOT deduplicated, deliberately. The shop has always
 * concatenated `image` with `images` and shown whatever that gives, and
 * quietly dropping a repeat here would be a change to the shop smuggled in
 * under a refactor. The Mum Hub's API is the one that overlaps the two fields
 * by design, and its adapter is where that is handled.
 */
export function cardProductFromProduct(product: Product): CardProduct {
  const price = product.price;
  const originalPrice = product.originalPrice;
  const originalPriceLabel = formatPrice(originalPrice);
  return {
    id: product.id,
    name: product.name,
    brand: product.brand,
    priceLabel: formatPrice(price) ?? "£0.00",
    originalPriceLabel:
      originalPrice && price != null && originalPrice > price && originalPriceLabel
        ? originalPriceLabel
        : null,
    images: [
      product.image,
      ...(((product as unknown as { images?: string[] }).images) ?? []),
    ],
    url: safeUrl((product as unknown as { url?: string }).url || "#"),
    affiliate: true,
    // The product id, as the app sends it, so a sale reads the same from
    // either surface. Only its first 12 characters reach `xcust`.
    affiliateId: product.id ?? null,
    // The products API sends no slug, so fall back to the id, as the Mum Hub
    // mapper below does; an undefined here broke click tracking.
    trackingSlug: product.slug || String(product.id),
  };
}

/**
 * A Mum Hub piece, as the card sees it.
 *
 * `affiliate` reads a missing field as `false`, which is the safe way round:
 * older API responses and fixtures predate the field, and the wrong default
 * restores the state where seven H&M pieces were wrapped for nothing.
 *
 * The photographs are deduplicated because the API sends `image` and a
 * separate `images` list that overlaps it, so the plain concatenation shows
 * the first photograph twice.
 */
export function cardProductFromPiece(item: MumHubUnlockedItem): CardProduct {
  // `piecePriceLabels` is the one place that decides how a piece's money
  // reads, including the all-or-nothing rule that a struck-through regular
  // price never appears without a price beside it.
  const price = piecePriceLabels(item);
  // `safeUrl` answers "#" for anything that is not http or https, and a "#"
  // href is an anchor that looks like every other tile and goes nowhere. Here
  // that is the same as having no link at all.
  const url = item.url ? safeUrl(item.url) : null;
  return {
    id: String(item.id),
    name: item.name,
    brand: item.brand,
    priceLabel: price?.current ?? null,
    originalPriceLabel: price?.original ?? null,
    images: pieceImages(item),
    url: url === "#" ? null : url,
    affiliate: item.affiliate === true,
    affiliateId: item.productId,
    trackingSlug: item.productId ?? String(item.id),
  };
}

/**
 * Every photograph of a piece, once, main one first.
 *
 * The API sends `image` and a separate `images` list and the two overlap, so a
 * naive `[image, ...images]` shows the first photograph twice: two slides, one
 * of them a lie about how many views of the garment there are.
 */
export function pieceImages(item: {
  image: string | null;
  images?: readonly string[] | null;
}): string[] {
  const out: string[] = [];
  for (const src of [item.image, ...(item.images ?? [])]) {
    if (!src) continue;
    if (out.includes(src)) continue;
    out.push(src);
  }
  return out;
}
